<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="900px"
    persistent
    class="agreement-dialog"
  >
    <v-card class="agreement-viewer">
      <!-- 标题栏 -->
      <v-card-title class="dialog-header">
        <span class="dialog-title">{{ currentAgreement && currentAgreement.title || '协议查看' }}</span>
        <v-btn
          icon
          small
          class="close-btn"
          @click="closeDialog"
          :disabled="(isInSequence && !canClose) || showChildrenList"
        >
          <v-icon size="20">close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- 协议内容 -->
      <v-card-text class="dialog-content">
        <div v-if="loading" class="loading-wrapper">
          <v-progress-circular
            indeterminate
            color="#dd122a"
          ></v-progress-circular>
          <p>正在加载协议内容...</p>
        </div>
        <div v-else-if="error" class="error-wrapper">
          <v-icon color="#999" size="48">error_outline</v-icon>
          <p>加载失败，请重试</p>
          <v-btn
            color="#dd122a"
            text
            @click="loadContent"
          >
            重新加载
          </v-btn>
        </div>
        <!-- 子条款列表模式 -->
        <div v-else-if="showChildrenList" class="children-list-wrapper">
          <div class="children-list">
            <div
              v-for="child in currentAgreement.children"
              :key="child.id"
              class="child-item"
              :class="{ 'viewed': viewedChildren.includes(child.id) }"
              @click="openChildAgreement(child)"
            >
              <span class="child-title">{{ child.title }}</span>
              <v-icon v-if="viewedChildren.includes(child.id)" color="#4caf50" size="20">check_circle</v-icon>
              <v-icon v-else color="#999" size="20">chevron_right</v-icon>
            </div>
          </div>
        </div>
        <!-- 子条款图片查看模式 -->
        <div v-else-if="isChildImage" class="image-wrapper" ref="childImageWrapper">
          <div class="child-header">
            <v-btn icon small @click="backToChildrenList">
              <v-icon>arrow_back</v-icon>
            </v-btn>
            <span class="child-title-text">{{ currentChild && currentChild.title }}</span>
            <span class="scroll-hint" v-if="!hasScrolledToBottom">
              <v-icon size="14">expand_more</v-icon>
              请滚动到底部
            </span>
            <span class="scroll-hint completed" v-else>
              <v-icon size="14" color="#4caf50">check_circle</v-icon>
              已读完
            </span>
          </div>
          <div class="image-container" ref="childImageContainer">
            <img :src="childImageUrl" alt="协议图片" class="agreement-image" @load="onImageLoad" @error="onImageError" />
          </div>
        </div>
        <!-- 多图片查看模式（子条款） -->
        <div v-else-if="showMultiImages && currentChild" class="image-wrapper">
          <div class="child-header">
            <v-btn icon small @click="backToChildrenList">
              <v-icon>arrow_back</v-icon>
            </v-btn>
            <span class="child-title-text">{{ currentChild && currentChild.title }}</span>
            <span class="scroll-hint" v-if="!hasScrolledToBottom">
              <v-icon size="14">expand_more</v-icon>
              请滚动到底部
            </span>
            <span class="scroll-hint completed" v-else>
              <v-icon size="14" color="#4caf50">check_circle</v-icon>
              已读完
            </span>
          </div>
          <div class="image-container" ref="childMultiImageContainer">
            <img 
              v-for="(img, index) in currentImages" 
              :key="index"
              :src="img"
              alt="协议图片"
              class="agreement-image"
              :class="{ 'current-image': index === currentImageIndex }"
              @load="onImageLoad"
              @error="onImageError"
            />
          </div>
        </div>
        <!-- 多图片查看模式（主协议） -->
        <div v-else-if="showMultiImages" class="image-wrapper">
          <div class="image-header">
            <span class="image-indicator">{{ currentImageIndex + 1 }} / {{ currentImages.length }}</span>
            <span class="scroll-hint" v-if="!hasScrolledToBottom">
              <v-icon size="14">expand_more</v-icon>
              请滚动到底部
            </span>
            <span class="scroll-hint completed" v-else>
              <v-icon size="14" color="#4caf50">check_circle</v-icon>
              已读完
            </span>
          </div>
          <div class="image-container" ref="multiImageContainer">
            <img 
              v-for="(img, index) in currentImages" 
              :key="index"
              :src="img"
              alt="协议图片"
              class="agreement-image"
              :class="{ 'current-image': index === currentImageIndex }"
              @load="onImageLoad"
              @error="onImageError"
            />
          </div>
        </div>
        <!-- 主协议单图片查看模式 -->
        <div v-else-if="isImage" class="image-wrapper" ref="imageWrapper">
          <div class="image-header">
            <span class="scroll-hint" v-if="!hasScrolledToBottom">
              <v-icon size="14">expand_more</v-icon>
              请滚动到底部
            </span>
            <span class="scroll-hint completed" v-else>
              <v-icon size="14" color="#4caf50">check_circle</v-icon>
              已读完
            </span>
          </div>
          <div class="image-container" ref="imageContainer">
            <img :src="imageUrl" alt="协议图片" class="agreement-image" @load="onImageLoad" @error="onImageError" />
          </div>
        </div>
        <div v-else class="agreement-content" ref="contentWrapper" v-html="content"></div>
      </v-card-text>

      <!-- 底部操作栏 -->
      <v-card-actions class="dialog-footer" v-if="isInSequence">
        <div class="progress-info">
          <span class="progress-text">{{ currentIndex + 1 }} / {{ totalCount }}</span>
          <div class="countdown-wrapper" v-if="showCountdown">
            <v-icon size="16" color="#dd122a">timer</v-icon>
            <span class="countdown-text">{{ countdown }}秒</span>
          </div>
        </div>
        <!-- 子条款列表模式下的按钮 -->
        <v-btn
          v-if="showChildrenList"
          color="#dd122a"
          class="next-btn"
          :disabled="listNextDisabled"
          @click="handleNext"
        >
          {{ isLast ? '同意并继续' : '下一步' }}
        </v-btn>
        <!-- 子条款多图片模式下的按钮（返回列表） -->
        <v-btn
          v-else-if="showMultiImages && currentChild"
          color="#dd122a"
          class="next-btn"
          :disabled="!childCanClose"
          @click="backToChildrenList"
        >
          返回列表
        </v-btn>
        <!-- 主协议多图片/单图片查看模式下的按钮 -->
        <v-btn
          v-else-if="showMultiImages || (isImage && !isChildImage)"
          color="#dd122a"
          class="next-btn"
          :disabled="!canClose"
          @click="handleNext()"
        >
          {{ isLast ? '同意并继续' : '下一步' }}
        </v-btn>
        <!-- HTML内容模式下的按钮（子条款查看时使用左上角返回箭头，不显示按钮） -->
        <v-btn
          v-else-if="content && !isChildImage && currentChild"
          color="#dd122a"
          class="next-btn"
          :disabled="!childCanClose"
          @click="backToChildrenList"
        >
          返回列表
        </v-btn>
        <!-- 普通模式下的按钮（子条款图片时不显示） -->
        <v-btn
          v-else-if="!isChildImage"
          color="#dd122a"
          class="next-btn"
          :disabled="!canClose"
          @click="handleNext"
        >
          {{ isLast ? '同意并继续' : '下一步' }}
        </v-btn>
      </v-card-actions>
      
      <!-- 单独查看时的关闭按钮 -->
      <v-card-actions class="dialog-footer" v-else>
        <v-spacer></v-spacer>
        <v-btn
          color="#dd122a"
          class="close-text-btn"
          text
          @click="closeDialog"
        >
          关闭
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// import { startReadingAgreement, finishReadingAgreement } from '@/utils/recordingUtils';

export default {
  name: 'AgreementViewer',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    agreement: {
      type: Object,
      default: null
    },
    isInSequence: {
      type: Boolean,
      default: false
    },
    currentIndex: {
      type: Number,
      default: 0
    },
    totalCount: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      content: '',
      loading: false,
      error: false,
      loadingAgreement: false,
      currentLoadingId: null,
      countdown: 0,
      countdownTimer: null,
      canClose: false,
      isImage: false,
      imageUrl: '',
      // 多图片相关
      showMultiImages: false,
      currentImages: [],
      currentImageIndex: 0,
      // 子条款相关
      showChildrenList: false,
      isChildImage: false,
      childImageUrl: '',
      currentChild: null,
      viewedChildren: [],
      childCanClose: false, // 子条款专用：当前子条款是否可返回
      listNextDisabled: true, // 子条款列表模式：下一步按钮是否禁用
      // 滚动检测相关
      hasScrolledToBottom: false,
      scrollListener: null,
      scrollContainer: null,
      imageContainer: null,
      totalScrollHeight: 0
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    currentAgreement() {
      return this.agreement;
    },
    showCountdown() {
      // 主协议倒计时或子条款倒计时
      const mainCountdown = this.currentAgreement && this.currentAgreement.requireTime && this.countdown > 0;
      const childCountdown = this.currentChild && this.currentChild.requireTime && this.countdown > 0;
      return this.isInSequence && (mainCountdown || childCountdown);
    },
    isLast() {
      return this.currentIndex === this.totalCount - 1;
    },
    // 子条款列表模式下：检查所有子条款是否都已查看
    allChildrenViewed() {
      if (!this.currentAgreement || !this.currentAgreement.children) {
        return true;
      }
      return this.currentAgreement.children.every(child => this.viewedChildren.includes(child.id));
    }
  },
  watch: {
    value(val) {
      if (val && this.currentAgreement) {
        this.loadContent();
      } else {
        this.clearCountdown();
        this.clearScrollListener();
      }
    },
    agreement: {
      handler(newVal, oldVal) {
        const oldId = oldVal ? oldVal.id : null;
        if (newVal && this.value && !this.loadingAgreement && newVal.id !== oldId) {
          this.loadContent();
        }
      },
      deep: true
    }
  },
  beforeDestroy() {
    this.clearCountdown();
    this.clearScrollListener();
  },
  methods: {
    async loadContent() {
      if (this.loadingAgreement) {
        return;
      }
      
      const agreementId = this.currentAgreement.id;
      this.loadingAgreement = true;
      this.currentLoadingId = agreementId;
      
      this.loading = true;
      this.error = false;
      this.canClose = false;
      this.countdown = 0;
      this.isImage = false;
      this.imageUrl = '';
      this.showMultiImages = false;
      this.currentImages = [];
      this.currentImageIndex = 0;
      this.showChildrenList = false;
      this.isChildImage = false;
      this.childImageUrl = '';
      this.currentChild = null;
      this.hasScrolledToBottom = false;
      
      this.clearScrollListener();
      
      try {
        // 判断是否为包含子条款的列表
        if (this.currentAgreement.isList && this.currentAgreement.children && this.currentAgreement.children.length > 0) {
          this.showChildrenList = true;
          this.listNextDisabled = true; // 进入列表时直接禁用下一步按钮
          this.loading = false;
          this.loadingAgreement = false;
          this.currentLoadingId = null;
          // startReadingAgreement(
          //   this.currentAgreement.id,
          //   this.currentAgreement.title
          // );
          this.checkAllChildrenViewed();
          return;
        }
        
        const url = this.currentAgreement.url;
        const images = this.currentAgreement.images;
        
        // 判断是否为多图片模式
        if (images && images.length > 0) {
          this.showMultiImages = true;
          this.currentImages = images;
          this.currentImageIndex = 0;
          this.loading = false;
          this.$nextTick(() => {
            this.setupImageScrollListener();
          });
          this.loadingAgreement = false;
          this.currentLoadingId = null;
          
          // 启动倒计时
          if (this.isInSequence && this.currentAgreement.requireTime && this.currentAgreement.duration > 0) {
            this.startCountdown(this.currentAgreement.duration);
          }
          
          return;
        }
        
        // 判断是否为单张图片
        if (this.isImageUrl(url)) {
          this.isImage = true;
          this.imageUrl = url;
          this.loading = false;
          this.$nextTick(() => {
            this.setupImageScrollListener();
          });
          this.loadingAgreement = false;
          this.currentLoadingId = null;
          
          // 启动倒计时
          if (this.isInSequence && this.currentAgreement.requireTime && this.currentAgreement.duration > 0) {
            this.startCountdown(this.currentAgreement.duration);
          }
          
          return;
        }
        
        // 非图片文件，尝试加载 HTML 内容
        const response = await this.fetchAgreementContent(url);
        if (this.currentLoadingId !== this.currentAgreement.id) {
          return;
        }
        this.content = response;
        this.loadingAgreement = false;
        this.currentLoadingId = null;
        
        this.$nextTick(() => {
          this.setupScrollListener();
        });
        
        if (this.isInSequence && this.currentAgreement.requireTime && this.currentAgreement.duration > 0) {
          this.startCountdown(this.currentAgreement.duration);
        }
      } catch (err) {
        this.error = true;
        this.loadingAgreement = false;
        this.currentLoadingId = null;
      } finally {
        this.loading = false;
      }
    },
    
    // 图片加载成功
    onImageLoad() {
      this.$nextTick(() => {
        this.setupImageScrollListener();
      });
    },
    
    // 图片加载失败
    onImageError() {
      this.error = true;
    },
    
    // 设置图片滚动监听
    setupImageScrollListener() {
      let container = null;
      // 子条款多图片模式
      if (this.showMultiImages && this.currentChild) {
        container = this.$refs.childMultiImageContainer;
      } else if (this.showMultiImages) {
        container = this.$refs.multiImageContainer;
      } else if (this.isChildImage) {
        container = this.$refs.childImageContainer;
      } else if (this.isImage) {
        container = this.$refs.imageContainer;
      }
      
      if (!container) {
        return;
      }
      
      this.clearScrollListener();
      
      // 等待图片加载完成后设置滚动监听
      setTimeout(() => {
        // 重新获取容器，因为 DOM 可能已更新
        let scrollContainer = null;
        if (this.showMultiImages && this.currentChild) {
          scrollContainer = this.$refs.childMultiImageContainer;
        } else if (this.showMultiImages) {
          scrollContainer = this.$refs.multiImageContainer;
        } else if (this.isChildImage) {
          scrollContainer = this.$refs.childImageContainer;
        } else if (this.isImage) {
          scrollContainer = this.$refs.imageContainer;
        }
        
        if (!scrollContainer) {
          return;
        }
        
        const handleScroll = () => {
          const scrollTop = scrollContainer.scrollTop;
          const scrollHeight = scrollContainer.scrollHeight;
          const clientHeight = scrollContainer.clientHeight;
          
          const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
          
          if (isAtBottom) {
            this.hasScrolledToBottom = true;
            // 子条款模式（单图片或多图片）
            if (this.currentChild) {
              if (this.currentChild.requireTime) {
                this.checkChildCanClose(this.currentChild.id);
              } else {
                this.childCanClose = true;
                this.markChildViewed(this.currentChild.id);
              }
            } else {
              this.checkCanClose();
            }
          } else {
            this.hasScrolledToBottom = false;
            if (this.currentChild && this.currentChild.requireTime) {
              this.childCanClose = false;
            }
          }
        };
        
        scrollContainer.addEventListener('scroll', handleScroll);
        this.scrollListener = handleScroll;
        this.scrollContainer = scrollContainer;
        this.totalScrollHeight = scrollContainer.scrollHeight;
      }, 500);
    },
    
    // 打开子条款
    openChildAgreement(child) {
      this.currentChild = child;
      this.showChildrenList = false;
      this.canClose = false;
      this.childCanClose = false;
      this.hasScrolledToBottom = false;
      
      // 清除子条款之前的倒计时
      this.clearCountdown();
      this.countdown = 0;
      
      // startReadingAgreement(child.id, child.title);
      
      // 通知父组件：子条款已打开，准备截图
      // this.$nextTick(() => {
      //   this.$emit('child-opened', child);
      // });
      
      // 检查子条款是否有 images 数组（多图片模式）
      if (child.images && child.images.length > 0) {
        this.isChildImage = false;
        this.showMultiImages = true;
        this.currentImages = child.images;
        this.currentImageIndex = 0;
        
        // 如果子条款需要倒计时，启动倒计时
        if (child.requireTime && child.duration > 0) {
          this.startChildCountdown(child.duration, child.id);
        }
        
        this.$nextTick(() => {
          this.setupImageScrollListener();
        });
        return;
      }
      
      // 判断子条款是否为单张图片
      if (this.isImageUrl(child.url)) {
        this.isChildImage = true;
        this.childImageUrl = child.url;
        this.showMultiImages = false;
        this.$nextTick(() => {
          this.setupImageScrollListener();
        });
      } else {
        // 非图片，加载HTML内容
        this.isChildImage = false;
        this.showMultiImages = false;
        this.fetchAgreementContent(child.url).then(content => {
          this.content = content;
          this.$nextTick(() => {
            this.setupScrollListener();
          });
          
          if (child.requireTime && child.duration > 0) {
            this.startChildCountdown(child.duration, child.id);
          }
        });
      }
    },
    
    // 子条款倒计时
    startChildCountdown(seconds, childId) {
      this.countdown = seconds;
      this.canClose = false;
      
      this.clearCountdown();
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.checkChildCanClose(childId);
          this.clearCountdown();
        }
      }, 1000);
    },
    
    // 标记子条款已查看
    markChildViewed(childId) {
      if (!this.viewedChildren.includes(childId)) {
        this.viewedChildren.push(childId);
      }
      this.checkAllChildrenViewed();
    },
    
    // 检查是否所有子条款都已查看
    checkAllChildrenViewed() {
      if (!this.currentAgreement || !this.currentAgreement.children || this.currentAgreement.children.length === 0) {
        // 没有子条款时，默认不允许关闭
        this.canClose = false;
        this.listNextDisabled = true;
        return;
      }
      const allViewed = this.currentAgreement.children.every(child => 
        this.viewedChildren.includes(child.id)
      );
      this.canClose = allViewed;
      this.listNextDisabled = !allViewed; // 更新列表按钮禁用状态
    },
    
    // 返回子条款列表
    backToChildrenList() {
      const child = this.currentChild;
      const scrollStatus = this.hasScrolledToBottom;
      const countdownStatus = !this.currentChild.requireTime || this.countdown <= 0;
      
      // 记录子条款阅读结果
      // if (child) {
      //   finishReadingAgreement({
      //     scrollToBottom: scrollStatus,
      //     countdownCompleted: countdownStatus
      //   });
      // }
      
      this.isChildImage = false;
      this.showChildrenList = true;
      this.showMultiImages = false;
      this.clearCountdown();
      this.countdown = 0;
      this.currentChild = null;
      this.childCanClose = false;
      this.hasScrolledToBottom = false;
      // 返回列表时不需要检查所有子条款是否都阅读完，用户可以自由返回
      // 只有在关闭对话框时才检查所有子条款是否都阅读完
    },
    
    // 设置滚动监听（普通模式）- 仅用于HTML内容
    setupScrollListener() {
      const wrapper = this.$refs.contentWrapper;
      if (!wrapper) return;
      
      this.clearScrollListener();
      
      const handleScroll = () => {
        const scrollTop = wrapper.scrollTop;
        const scrollHeight = wrapper.scrollHeight;
        const clientHeight = wrapper.clientHeight;
        
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
        
        if (isAtBottom) {
          this.hasScrolledToBottom = true;
          this.checkCanClose();
        }
      };
      
      wrapper.addEventListener('scroll', handleScroll);
      this.scrollListener = handleScroll;
      this.scrollContainer = wrapper;
    },
    
    // 清除滚动监听
    clearScrollListener() {
      if (this.scrollListener && this.scrollContainer) {
        this.scrollContainer.removeEventListener('scroll', this.scrollListener);
        this.scrollListener = null;
        this.scrollContainer = null;
      }
    },
    
    // 检查是否可以关闭
    checkCanClose() {
      const timeCondition = !this.currentAgreement.requireTime || this.countdown <= 0;
      const scrollCondition = this.hasScrolledToBottom;
      
      if (scrollCondition && timeCondition) {
        this.canClose = true;
      }
    },
    
    // 检查子条款是否可以关闭
    checkChildCanClose(childId) {
      const timeCondition = !this.currentChild.requireTime || this.countdown <= 0;
      const scrollCondition = this.hasScrolledToBottom;
      
      if (scrollCondition && timeCondition) {
        this.childCanClose = true;
        this.markChildViewed(childId);
      }
    },
    
    // 判断是否为图片链接
    isImageUrl(url) {
      if (!url) return false;
      const lowerUrl = url.toLowerCase();
      const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
      return imageExtensions.some(ext => lowerUrl.endsWith(ext) || lowerUrl.includes(ext + '?'));
    },
    
    // 从OSS获取内容
    fetchAgreementContent(url) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(`
            <div class="agreement-doc">
              <h2>${(this.currentAgreement && this.currentAgreement.title) || '协议内容'}</h2>
              <p>这是协议内容区域，实际内容将从OSS链接加载：</p>
              <p style="color: #999; margin-top: 20px;">${url}</p>
              <div style="margin-top: 30px; line-height: 2; color: #666;">
                <p>1. 协议条款内容...</p>
                <p>2. 协议条款内容...</p>
                <p>3. 协议条款内容...</p>
                <p>（此处为示例内容，实际使用时请替换为真实的OSS文件内容）</p>
              </div>
            </div>
          `);
        }, 500);
      });
    },
    
    startCountdown(seconds) {
      this.countdown = seconds;
      this.canClose = false;
      
      this.clearCountdown();
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          this.checkCanClose();
          this.clearCountdown();
        }
      }, 1000);
    },
    
    clearCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    },
    
    closeDialog() {
      if (this.isInSequence && !this.canClose) {
        return;
      }
      
      if (!this.isInSequence && this.canClose) {
        this.$emit('viewed');
      }
      
      this.clearCountdown();
      this.clearScrollListener();
      this.resetChildState();
      this.hasScrolledToBottom = false;
      this.dialogVisible = false;
      this.$emit('close');
    },
    
    resetChildState() {
      this.showChildrenList = false;
      this.isChildImage = false;
      this.childImageUrl = '';
      this.currentChild = null;
      this.viewedChildren = [];
      this.listNextDisabled = true;
    },
    
    handleNext() {
      // 记录当前协议阅读结果
      // if (this.currentAgreement && !this.isLast) {
      //   finishReadingAgreement({
      //     scrollToBottom: this.hasScrolledToBottom,
      //     countdownCompleted: !this.currentAgreement.requireTime || this.countdown <= 0
      //   });
      // }
      
      this.clearCountdown();
      this.clearScrollListener();
      this.resetChildState();
      this.hasScrolledToBottom = false;
      this.canClose = false;
      
      this.$nextTick(() => {
        // 滚动条回到顶部
        const containers = [
          this.$refs.multiImageContainer,
          this.$refs.childMultiImageContainer,
          this.$refs.imageContainer,
          this.$refs.childImageContainer,
          this.$refs.contentWrapper
        ];
        containers.forEach(container => {
          if (container) {
            container.scrollTop = 0;
          }
        });
        
        if (this.isLast) {
          this.finishReadingAndContinueRecording();
          this.$emit('complete');
        } else {
          this.$emit('next');
        }
      });
    },
    
    // 完成当前协议阅读（继续录制，不停止）
    finishReadingAndContinueRecording() {
      // if (this.currentAgreement) {
      //   finishReadingAgreement({
      //     scrollToBottom: this.hasScrolledToBottom,
      //     countdownCompleted: !this.currentAgreement.requireTime || this.countdown <= 0
      //   });
      // }
      
      // 注意：这里不停止录制，让录制继续
      // 录制将在 planSelection.vue 提交订单时停止
      console.log('协议阅读完成，录制继续进行');
      
      // this.$emit('complete');
    },
  }
};
</script>

<style lang="scss" scoped>
.agreement-viewer {
  border-radius: 8px;
  overflow: hidden;
  
  .dialog-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid #e8e8e8;
    background-color: #fff;
    
    .dialog-title {
      font-size: 16px;
      font-weight: 500;
      color: #333;
    }
    
    .close-btn {
      margin: 0;
      color: #999;
    }
  }
  
  .dialog-content {
    padding: 0;
    max-height: 65vh;
    overflow: hidden;
    
    .loading-wrapper,
    .error-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      color: #999;
      max-height: 65vh;
      overflow-y: auto;
      
      p {
        margin-top: 16px;
        font-size: 14px;
      }
    }
    
    .agreement-content {
      padding: 24px;
      font-size: 14px;
      line-height: 1.8;
      color: #333;
      max-height: 65vh;
      overflow-y: auto;
      scrollbar-width: thin;
      scrollbar-color: #ddd transparent;
      
      &::-webkit-scrollbar {
        width: 6px;
      }
      
      &::-webkit-scrollbar-thumb {
        background-color: #ddd;
        border-radius: 3px;
      }
      
      ::v-deep .agreement-doc {
        h2 {
          font-size: 18px;
          font-weight: 500;
          margin-bottom: 20px;
          text-align: center;
        }
        
        p {
          margin-bottom: 12px;
        }
      }
    }
    
    .image-wrapper {
      position: relative;
      background-color: #f5f5f5;
      
      .image-header {
        position: sticky;
        top: 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 16px;
        padding: 8px 16px;
        background-color: #fff;
        border-bottom: 1px solid #e8e8e8;
        z-index: 10;
        
        .image-indicator {
          font-size: 14px;
          color: #666;
        }
      }
      
      .child-header {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        background-color: #fff;
        border-bottom: 1px solid #e8e8e8;
        
        .child-title-text {
          margin-left: 12px;
          font-size: 14px;
          font-weight: 500;
          color: #333;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
      
      .scroll-hint {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 6px 12px;
        background-color: #fff3e0;
        border-radius: 16px;
        font-size: 12px;
        color: #e65100;
        margin-left: auto;
        
        &.completed {
          background-color: #e8f5e9;
          color: #2e7d32;
        }
      }
      
      .image-container {
        position: relative;
        height: 65vh;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: #ccc transparent;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 16px;
        
        &::-webkit-scrollbar {
          width: 8px;
        }
        
        &::-webkit-scrollbar-thumb {
          background-color: #ccc;
          border-radius: 4px;
        }
        
        .agreement-image {
          max-width: 100%;
          display: block;
          margin-bottom: 10px;
        }
      }
    }
    
    .children-list-wrapper {
      padding: 16px;
      max-height: 65vh;
      overflow-y: auto;
      
      .children-list {
        .child-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background-color: #f9f9f9;
          border-radius: 8px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.3s;
          
          &:hover {
            background-color: #f0f0f0;
          }
          
          &.viewed {
            background-color: #e8f5e9;
          }
          
          .child-title {
            font-size: 14px;
            color: #333;
            flex: 1;
            margin-right: 12px;
            line-height: 1.5;
          }
        }
      }
    }
  }
  
  .dialog-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    border-top: 1px solid #e8e8e8;
    background-color: #f9f9f9;
    
    .progress-info {
      display: flex;
      align-items: center;
      gap: 16px;
      
      .progress-text {
        font-size: 14px;
        color: #666;
      }
      
      .countdown-wrapper {
        display: flex;
        align-items: center;
        gap: 4px;
        color: #dd122a;
        font-size: 14px;
      }
    }
    
    .next-btn {
      text-transform: none;
      font-size: 14px;
      height: 36px;
      min-width: 100px;
      border-radius: 4px;
      color: #fff;
    }
    
    .close-text-btn {
      color: #fff !important;
    }
  }
}
</style>
